import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Portal,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Logout,
  WorkHistory,
  Redeem,
  VideoCall,
  Message,
  Security,
  ShoppingCart,
  ChevronRight,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "@/context/AuthContext";
import { useCompanyConfigContext } from "@/context/CompanyConfigContext";
import { usePrpNavigation } from "./PrpNavigationContext";
import { useProgramContext } from "@/context/ProgramContext";
import { PointSourcesBadges } from "./PointSourcesBadges";
import { keyframes } from "@emotion/react";

// Reusable animations
// const fadeIn = keyframes`
//   from {
//     opacity: 0;
//   }
//   to {
//     opacity: 1;
//   }
// `;

// const fadeOut = keyframes`
//   from {
//     opacity: 1;
//   }
//   to {
//     opacity: 0;
//   }
// `;

const slideUp = keyframes`
  from {
    transform: translate(20px, 20px);
    opacity: 0;
  }
  to {
    transform: translate(0, 0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translate(0, 0);
    opacity: 1;
  }
  to {
    transform: translate(20px, 20px);
    opacity: 0;
  }
`;

const slideInFromLeft = keyframes`
  from {
    transform: translateX(-10px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(10px);
    opacity: 0;
  }
`;

// Animation durations
const ANIMATION_DURATION = "0.3s";

export const PrpHome = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { company } = useCompanyConfigContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isOpen, toggleNavigation } = usePrpNavigation();
  const { programs } = useProgramContext();

  const handleMenuClose = () => {
    toggleNavigation();
  };

  const navigateTo = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  return (
    <Portal>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Drawer
          anchor="bottom"
          open={isOpen}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              position: "absolute",
              width: "100%",
              height: "100%",
              paddingTop: 2,
              paddingBottom: 2,
              animation: isOpen
                ? `${slideUp} ${ANIMATION_DURATION} ease-in-out forwards`
                : `${slideDown} ${ANIMATION_DURATION} ease-in-out forwards`,
            },
          }}
          ModalProps={{
            hideBackdrop: true,
            sx: {
              top: isMobile ? "0" : "90px",
              bottom: isMobile ? "6.9rem" : "0",
              zIndex: 1400,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              alignItems: isMobile ? "center" : "flex-start",
              paddingLeft: isMobile ? 0 : 4,
              paddingRight: isMobile ? 0 : 4,
              animation: isOpen
                ? `${slideInFromLeft} ${ANIMATION_DURATION} ease-in-out forwards`
                : `${slideOutToRight} ${ANIMATION_DURATION} ease-in-out forwards`,
            }}
          >
            <Box
              sx={{
                textAlign: isMobile ? "center" : "left",
                padding: 2,
              }}
            >
              <Typography variant="h5">
                Hello, {user.firstName} {user.lastName}!
              </Typography>
              <Typography variant="body2" sx={{ color: "grey" }}>
                {user.email}
              </Typography>
              <Box
                onClick={() => navigateTo("/shop/history")}
                data-cypress="points-activity-link"
                sx={{
                  maxWidth: "175px",
                  width: "100%",
                  cursor: "pointer",
                  mt: 2,
                  marginLeft: {
                    xs: "auto",
                    sm: 0,
                  },
                  marginRight: {
                    xs: "auto",
                    sm: "auto",
                  },
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    "& .points-card": {
                      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.7)",
                    },
                    "& .view-activity": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  },
                }}
              >
                <Box
                  className="points-card"
                  sx={{
                    backgroundColor: "#000000",
                    borderRadius: "6.5px",
                    width: "100%",
                    minHeight: "110px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.6)",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: 1,
                      position: "relative",
                      top: "-3px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        position: "relative",
                        top: "-2px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            color: "#ffffff",
                            fontWeight: 300,
                            fontSize: "2rem",
                          }}
                        >
                          {user.points.toLocaleString("en-US", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                          })}
                        </Typography>
                        <Box
                          component="img"
                          src={"/images/leaf.png"} // Just leaving this hardcoded for now
                          alt={"points"}
                          sx={{
                            width: 48,
                            height: 48,
                            objectFit: "contain",
                            position: "relative",
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffffff",
                          fontSize: "0.9rem",
                          letterSpacing: "0.5px",
                          height: 0,
                          position: "relative",
                          top: "-8px",
                        }}
                      >
                        Balance Available
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  className="view-activity"
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    color: theme.palette.secondary.main,
                    padding: 1,
                    borderRadius: 1,
                    transition: "background-color 0.2s ease-in-out",
                  }}
                >
                  <Typography variant="body2">View Points Activity</Typography>
                  <ChevronRight fontSize="small" />
                </Box>
              </Box>

              {user.pointSources && company?.pointSources && (
                <PointSourcesBadges
                  userPointSources={user.pointSources}
                  companyPointSources={company.pointSources}
                />
              )}
            </Box>

            <Divider sx={{ width: isMobile ? "80%" : "100%", mb: 2 }} />

            <List
              sx={{
                width: isMobile ? "80%" : "100%",
                maxWidth: 360,
                display: "flex",
                flexDirection: "column",
                alignItems: isMobile ? "center" : "flex-start",
              }}
            >
              {user.role === "super-admin" && (
                <ListItem
                  component="div"
                  data-cypress="super-admin-dashboard-link"
                  onClick={() => navigateTo("/super-admin")}
                  sx={{
                    justifyContent: isMobile ? "center" : "flex-start",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isMobile ? "auto" : 56,
                      mr: isMobile ? 1 : 0,
                    }}
                  >
                    <Security />
                  </ListItemIcon>
                  <ListItemText
                    data-cypress="super-admin-dashboard-link"
                    primary="Super Admin Dashboard"
                  />
                </ListItem>
              )}

              <ListItem
                component="div"
                onClick={() => navigateTo("/shop")}
                sx={{
                  justifyContent: isMobile ? "center" : "flex-start",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isMobile ? "auto" : 56,
                    mr: isMobile ? 1 : 0,
                  }}
                >
                  <ShoppingCart />
                </ListItemIcon>
                <ListItemText data-cypress="shop-link" primary="Redeem IT" />
              </ListItem>

              {company?.features?.raves && (
                <ListItem
                  component="div"
                  onClick={() => navigateTo("/raves")}
                  sx={{
                    justifyContent: isMobile ? "center" : "flex-start",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isMobile ? "auto" : 56,
                      mr: isMobile ? 1 : 0,
                    }}
                  >
                    <VideoCall />
                  </ListItemIcon>
                  <ListItemText
                    data-cypress="raves-link"
                    primary="Rave about IT"
                  />
                </ListItem>
              )}

              {company?.features?.tasker &&
                company?.features?.tasker?.status === "active" && (
                  <ListItem
                    component="div"
                    onClick={() => navigateTo("/sendit")}
                    sx={{
                      justifyContent: isMobile ? "center" : "flex-start",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: isMobile ? "auto" : 56,
                        mr: isMobile ? 1 : 0,
                      }}
                    >
                      <Message fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      data-cypress="tasker-link"
                      primary="Send IT"
                    />
                  </ListItem>
                )}

              {company?.features?.codeRedemption && (
                <ListItem
                  component="div"
                  onClick={() => {
                    handleMenuClose();
                  }}
                  sx={{
                    justifyContent: isMobile ? "center" : "flex-start",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isMobile ? "auto" : 56,
                      mr: isMobile ? 1 : 0,
                    }}
                  >
                    <Redeem fontSize="small" />
                  </ListItemIcon>
                  <ListItemText data-cypress="redeem-link" primary="Redeem" />
                </ListItem>
              )}

              {programs.some((p) => p.features.includes("liteRaves")) && (
                <ListItem
                  component="div"
                  onClick={() => navigateTo("/lite-raves")}
                  sx={{
                    justifyContent: isMobile ? "center" : "flex-start",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isMobile ? "auto" : 56,
                      mr: isMobile ? 1 : 0,
                    }}
                  >
                    <Redeem fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    data-cypress="lite-raves-link"
                    primary="Dealer Loaders"
                  />
                </ListItem>
              )}

              <ListItem
                component="div"
                onClick={() => navigateTo("/shop/history")}
                sx={{
                  justifyContent: isMobile ? "center" : "flex-start",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isMobile ? "auto" : 56,
                    mr: isMobile ? 1 : 0,
                  }}
                >
                  <WorkHistory fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  data-cypress="purchase-history-link"
                  primary="Purchase History"
                />
              </ListItem>

              <ListItem
                component="div"
                onClick={() => navigateTo("/logout")}
                sx={{
                  justifyContent: isMobile ? "center" : "flex-start",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                }}
                data-cypress="logout-button"
              >
                <ListItemIcon
                  sx={{
                    minWidth: isMobile ? "auto" : 56,
                    mr: isMobile ? 1 : 0,
                  }}
                >
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText data-cypress="logout-link" primary="Logout" />
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </Box>
    </Portal>
  );
};
